<template>
    <div class="compass-card compass-card--accent">
        <div class="security-header">
            <h5 class="mb-0">
                <span class="security-icon">&#x1F512;</span>
                Token Security
            </h5>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <template v-else>
            <div class="security-checks">
                <!-- Mint Authority - condensed to single line -->
                <div class="security-item">
                    <span class="check-label">Mint Authority</span>
                    <span v-if="!securityData.is_mintable" class="check-status status-safe">
                        <span class="status-icon">✓</span>
                        Disabled
                    </span>
                    <a v-else
                       :href="`https://solscan.io/account/${securityData.mint_authority}`"
                       target="_blank"
                       class="check-status authority-link"
                       :class="mintAuthorityClass"
                       :title="mintAuthorityTooltip">
                        <span class="status-icon">{{ mintAuthorityIcon }}</span>
                        {{ truncateAddress(securityData.mint_authority) }}
                    </a>
                </div>

                <!-- Freeze Authority - condensed to single line -->
                <div class="security-item">
                    <span class="check-label">Freeze Authority</span>
                    <span v-if="!securityData.is_freezable" class="check-status status-safe">
                        <span class="status-icon">✓</span>
                        Disabled
                    </span>
                    <a v-else
                       :href="`https://solscan.io/account/${securityData.freeze_authority}`"
                       target="_blank"
                       class="check-status authority-link"
                       :class="freezeAuthorityClass"
                       :title="freezeAuthorityTooltip">
                        <span class="status-icon">{{ freezeAuthorityIcon }}</span>
                        {{ truncateAddress(securityData.freeze_authority) }}
                    </a>
                </div>

                <!-- Top 10 Holders -->
                <div class="security-item" v-if="securityData.top_10_percent !== null">
                    <span class="check-label">Top 10 Holders</span>
                    <span class="check-status" :class="top10Status.class">
                        <span class="status-icon">{{ top10Status.icon }}</span>
                        {{ top10Status.value }}
                    </span>
                </div>
            </div>

            <!-- Social Links Section -->
            <div class="social-links" v-if="hasLinks">
                <div class="links-row">
                    <a v-if="securityData.website" :href="normalizeUrl(securityData.website)" target="_blank" rel="noopener nofollow" class="social-link" title="Website">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                    </a>
                    <a v-if="securityData.twitter" :href="normalizeTwitterUrl(securityData.twitter)" target="_blank" rel="noopener nofollow" class="social-link" title="Twitter/X">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                    </a>
                    <a v-if="securityData.discord" :href="normalizeDiscordUrl(securityData.discord)" target="_blank" rel="noopener nofollow" class="social-link" title="Discord">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                        </svg>
                    </a>
                    <a v-if="securityData.telegram" :href="normalizeTelegramUrl(securityData.telegram)" target="_blank" rel="noopener nofollow" class="social-link" title="Telegram">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                        </svg>
                    </a>
                    <a v-if="securityData.github" :href="normalizeGithubUrl(securityData.github)" target="_blank" rel="noopener nofollow" class="social-link" title="GitHub">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                    </a>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'TokenSecurityPanel',

    props: {
        mint: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            loading: true,
            securityData: {
                mint_authority: null,
                freeze_authority: null,
                is_mintable: false,
                is_freezable: false,
                mint_authority_is_pda: null,
                freeze_authority_is_pda: null,
                top_10_percent: null,
                website: null,
                twitter: null,
                discord: null,
                telegram: null,
                github: null
            }
        };
    },

    computed: {
        // Mint authority styling and tooltip
        mintAuthorityClass() {
            // PDA = program controlled (yellow), regular address = wallet (red)
            if (this.securityData.mint_authority_is_pda === true) {
                return 'status-warning';
            }
            return 'status-danger';
        },

        mintAuthorityIcon() {
            if (this.securityData.mint_authority_is_pda === true) {
                return '⚙';
            }
            return '⚠';
        },

        mintAuthorityTooltip() {
            if (this.securityData.mint_authority_is_pda === true) {
                return 'Program Derived Address (PDA) - controlled by on-chain program rules';
            }
            return 'Wallet address - owner can mint tokens at any time';
        },

        // Freeze authority styling and tooltip
        freezeAuthorityClass() {
            if (this.securityData.freeze_authority_is_pda === true) {
                return 'status-warning';
            }
            return 'status-danger';
        },

        freezeAuthorityIcon() {
            if (this.securityData.freeze_authority_is_pda === true) {
                return '⚙';
            }
            return '⚠';
        },

        freezeAuthorityTooltip() {
            if (this.securityData.freeze_authority_is_pda === true) {
                return 'Program Derived Address (PDA) - controlled by on-chain program rules';
            }
            return 'Wallet address - owner can freeze accounts at any time';
        },

        top10Status() {
            const percent = this.securityData.top_10_percent;
            if (percent === null) {
                return { value: 'N/A', icon: '?', class: 'status-unknown' };
            }
            const safe = percent < 30;
            const danger = percent > 50;
            return {
                value: percent.toFixed(1) + '%',
                icon: safe ? '✓' : (danger ? '✗' : '⚠'),
                class: safe ? 'status-safe' : (danger ? 'status-danger' : 'status-warning')
            };
        },

        hasLinks() {
            return this.securityData.website ||
                   this.securityData.twitter ||
                   this.securityData.discord ||
                   this.securityData.telegram ||
                   this.securityData.github;
        }
    },

    mounted() {
        this.fetchSecurityData();
    },

    watch: {
        mint() {
            this.fetchSecurityData();
        }
    },

    methods: {
        async fetchSecurityData() {
            this.loading = true;
            try {
                const response = await fetch(`/api/tokens/${this.mint}/security`);
                if (response.ok) {
                    const data = await response.json();
                    this.securityData = data;
                }
            } catch (error) {
                console.error('Failed to fetch security data:', error);
            } finally {
                this.loading = false;
            }
        },

        truncateAddress(address) {
            if (!address) return 'N/A';
            return `${address.slice(0, 4)}...${address.slice(-4)}`;
        },

        normalizeUrl(url) {
            if (!url) return '#';
            if (url.startsWith('http://') || url.startsWith('https://')) return url;
            return 'https://' + url;
        },

        normalizeTwitterUrl(handle) {
            if (!handle) return '#';
            if (handle.startsWith('http')) return handle;
            const cleanHandle = handle.replace(/^@/, '');
            return `https://x.com/${cleanHandle}`;
        },

        normalizeDiscordUrl(url) {
            if (!url) return '#';
            if (url.startsWith('http')) return url;
            return `https://discord.gg/${url}`;
        },

        normalizeTelegramUrl(handle) {
            if (!handle) return '#';
            if (handle.startsWith('http')) return handle;
            const cleanHandle = handle.replace(/^@/, '');
            return `https://t.me/${cleanHandle}`;
        },

        normalizeGithubUrl(url) {
            if (!url) return '#';
            if (url.startsWith('http')) return url;
            return `https://github.com/${url}`;
        }
    }
};
</script>

<style scoped>
.compass-card {
    background: var(--compass-bg-secondary, #1a1a2e);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    border-radius: var(--compass-radius, 12px);
    padding: 1.25rem;
}

.compass-card--accent {
    border-top: 3px solid transparent;
    border-image: var(--compass-gradient, linear-gradient(135deg, #8b5cf6, #ec4899)) 1;
}

.security-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
}

.security-header h5 {
    font-size: 1rem;
    font-weight: 600;
    color: var(--compass-text-primary, #e6edf3);
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.security-icon {
    font-size: 1.1rem;
}

.security-checks {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.security-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
}

.check-label {
    color: var(--compass-text-secondary, #7d8590);
}

.check-status {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-weight: 500;
}

.authority-link {
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.15s ease;
}

.authority-link:hover {
    opacity: 0.8;
    text-decoration: underline;
}

.status-icon {
    font-size: 0.85rem;
}

.status-safe {
    color: var(--compass-success, #22c55e);
}

.status-warning {
    color: #f59e0b;
}

.status-danger {
    color: var(--compass-danger, #ef4444);
}

.status-unknown {
    color: var(--compass-text-secondary, #7d8590);
}

/* Social Links Section */
.social-links {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--compass-border, rgba(139, 92, 246, 0.1));
}

.links-row {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
}

.social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: rgba(139, 92, 246, 0.1);
    color: var(--compass-text-secondary, #7d8590);
    text-decoration: none;
    transition: all 0.2s ease;
}

.social-link:hover {
    background: rgba(139, 92, 246, 0.2);
    color: var(--compass-purple, #8b5cf6);
    transform: translateY(-1px);
}

.social-link svg {
    flex-shrink: 0;
}

/* Light mode support */
body:not(.dark-mode) .compass-card {
    background: #ffffff;
}

body:not(.dark-mode) .security-header h5 {
    color: #1f2937;
}

body:not(.dark-mode) .check-label {
    color: #6b7280;
}

body:not(.dark-mode) .social-link {
    background: rgba(139, 92, 246, 0.05);
}

body:not(.dark-mode) .social-link:hover {
    background: rgba(139, 92, 246, 0.1);
}
</style>
